import { motion } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import master from '../../../assets/pages/payment/mastercard.png';
import visa from '../../../assets/pages/payment/visa.png';
import amex from '../../../assets/pages/payment/amex.png';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../auth/Auth';
import { getSubscriptionDetails } from '../../../auth/_requests';
import { SubscriptionDetail } from '../../utils/types';
import { Spinner } from '../../UI/Spinner/Spinner';
import { formatDateToDisplayUTCLong } from '../../utils/utils';
import { useNavigate } from 'react-router-dom';

const SubscriptionDetails = () => {
  const { setModal, modalState } = useModal();
  const navigate = useNavigate();
  const tenantBuildingId = modalState?.tenantBuildingId;
  const tenantMitSubscriptionId = modalState?.tenantMitSubscriptionId;
  const [loading, setLoading] = useState<boolean>(false);
  const { auth } = useAuth();
  const [detailNotFound, setDetailNotFound] = useState<boolean>(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetail>();

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        setLoading(true);
        const response = await getSubscriptionDetails(auth?.data.token, tenantMitSubscriptionId);
        if (response) {
          setLoading(false);
          setSubscriptionDetails(response.data);
        }
      } catch (error) {
        if (
          typeof error === 'object' &&
          error !== null &&
          'message' in error &&
          typeof (error as { message?: unknown }).message === 'string' &&
          ((error as { message: string }).message === 'tenant_payment_option_not_found' ||
            (error as { message: string }).message === 'tenant_mit_subscription_not_found')
        ) {
          setDetailNotFound(true);
        }
        console.error('Error fetching subscription details:', error);
      } finally {
        setLoading(false);
      }
    };

    if (tenantMitSubscriptionId) {
      fetchSubscriptionDetails();
    }
  }, [auth?.data.token, tenantMitSubscriptionId]);

  if (loading) {
    return <Spinner widthFull='!bg-transparent' />;
  }

  return (
    <motion.div
      className='relative w-[554px] bg-[#FFF] rounded-[12px] flex flex-col overflow-auto'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className='flex justify-end p-4'>
        <X
          className='cursor-pointer text-gray-500 hover:text-gray-700'
          size={24}
          onClick={() => setModal(null)}
        />
      </div>

      <div className='flex flex-col gap-4 px-10 pb-8 '>
        {tenantMitSubscriptionId && subscriptionDetails && (
          <>
            <div className='flex flex-col justify-center items-center gap-1'>
              {curlyCheck}
              <span className='font-bold text-[22px] text-success'>Autopay confirmed!</span>
              <span className='text-main'>Your Autopay has been set up successfully</span>
            </div>
            <div className='flex flex-col px-6 py-8 items-start justify-center gap-5 rounded-[20px] bg-[rgba(33,81,236,0.06)]'>
              <div className='flex flex-col gap-2 w-full'>
                <div className='grid gap-y-2 w-full'>
                  <div className='grid grid-cols-2 justify-between'>
                    <span className='text-light/90'>Rent amount</span>
                    <span className='text-right text-main font-semibold'>
                      ${subscriptionDetails?.rentAmount}
                    </span>
                  </div>
                  <div className='grid grid-cols-2 justify-between'>
                    <div className='flex flex-col'>
                      <span className='text-light/90'>Processing fee</span>
                      <span className='text-light/50 text-xs'>
                        {' '}
                        {subscriptionDetails?.processingFee?.fixed !== '0' && (
                          <span>
                            Fixed processing fee: ${subscriptionDetails?.processingFee?.fixed}
                          </span>
                        )}
                        {subscriptionDetails?.processingFee?.percentageRate !== '0' && (
                          <span>
                            Processing fee {subscriptionDetails?.processingFee?.percentageRate}%: $
                            {subscriptionDetails?.processingFee?.percentage}{' '}
                          </span>
                        )}
                      </span>
                    </div>

                    <span className='text-right text-main font-semibold'>
                      ${subscriptionDetails?.processingFee?.total}
                    </span>
                  </div>
                  <div className='grid grid-cols-2 justify-between'>
                    <span className='text-light/90'>Total amount</span>
                    <span className='text-right text-main font-bold'>
                      ${subscriptionDetails?.totalAmount?.toLocaleString()}
                    </span>
                  </div>
                  <div className='grid grid-cols-2 justify-between'>
                    <span className='text-light/90'>Payment method</span>
                    <span className='text-right text-main font-semibold'>
                      {subscriptionDetails?.paymentMethod?.toLowerCase() === 'preauthorizeddebit'
                        ? 'Pre-authorized debit'
                        : subscriptionDetails?.paymentMethod}
                    </span>
                  </div>
                  {subscriptionDetails?.cardCompanyType && (
                    <div className='grid grid-cols-2 justify-between'>
                      <span className='text-light/90'>Card type</span>
                      <span className='text-right text-main font-semibold flex justify-end items-center gap-2'>
                        <img
                          src={(() => {
                            switch (subscriptionDetails?.cardCompanyType) {
                              case 'Mastercard':
                                return master;
                              case 'Visa':
                                return visa;
                              case 'Amex':
                                return amex;
                              default:
                                return '';
                            }
                          })()}
                          alt='card'
                          className='max-w-[30px]'
                        />
                        {subscriptionDetails?.cardCompanyType}
                      </span>
                    </div>
                  )}
                  <div className='grid grid-cols-2 justify-between'>
                    <span className='text-light/90'>Due date</span>
                    <span className='text-right text-main font-semibold'>
                      {subscriptionDetails?.dueDate}
                    </span>
                  </div>
                  <div className='grid grid-cols-2 justify-between'>
                    <span className='text-light/90'>Autopay enabled on</span>
                    <span className='text-right text-main font-semibold'>
                      {formatDateToDisplayUTCLong(subscriptionDetails?.timestampAdded)}
                    </span>
                  </div>
                </div>
              </div>
              {/*   <button className='h-[52px] rounded-[10px] w-full px-4 py-3 flex items-center justify-center bg-[rgba(33,81,236,0.08)]'>
                <div className='flex justify-between w-full p-4'>
                  <span className='text-primary text-base font-semibold'>
                    Setup Credit Reporting
                  </span>
                  <ArrowRight className='text-primary h-5 w-5' />
                </div>
              </button> */}
            </div>
          </>
        )}

        {!tenantMitSubscriptionId && tenantBuildingId && (
          <div className='flex flex-col px-6 py-8 items-start justify-center gap-5 rounded-[20px] bg-[rgba(255,0,0,0.06)]'>
            <span className='text-failure text-[22px] font-bold'>Autopay setup failed</span>
            <span className='text-main text-sm'>
              We couldn't complete your Autopay setup. This may be due to a payment issue
              or a technical glitch. Please try again or contact support for help.
            </span>
            <button
              className='submitBtn mt-4'
              onClick={() => {
                navigate('/pay-now', {
                  state: { buildingId: tenantBuildingId, initialStep: 1, payPlan: 'autopay' },
                });
                setModal(null);
              }}
            >
              <div className='flex justify-between w-full p-4'>
                <span>Retry autopay setup</span>
                <ArrowRight className=' h-5 w-5' />
              </div>
            </button>
          </div>
        )}

        {detailNotFound && (
          <div className='flex flex-col px-6 py-8 items-start justify-center gap-5 rounded-[20px] bg-[rgba(255,0,0,0.06)]'>
            <span className='text-failure text-[22px] font-bold'>Autopay disabled</span>
            <span className='text-main text-sm'>Autopay has been disabled for this building.</span>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export { SubscriptionDetails };

const curlyCheck = (
  <svg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40' fill='none'>
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M16.6111 3.53119C18.4292 1.53485 21.5704 1.53485 23.3885 3.5312L25.0888 5.39825C25.5065 5.85686 26.1068 6.10551 26.7264 6.07655L29.2489 5.95866C31.9461 5.83261 34.1673 8.05376 34.0412 10.751L33.9233 13.2735C33.8944 13.8931 34.143 14.4934 34.6016 14.911L36.4687 16.6114C38.465 18.4295 38.465 21.5706 36.4687 23.3887L34.6016 25.0891C34.143 25.5067 33.8944 26.107 33.9233 26.7266L34.0412 29.2491C34.1673 31.9463 31.9461 34.1675 29.2489 34.0414L26.7264 33.9236C26.1068 33.8946 25.5065 34.1432 25.0888 34.6019L23.3885 36.4689C21.5704 38.4653 18.4292 38.4652 16.6111 36.4689L14.9108 34.6019C14.4931 34.1432 13.8929 33.8946 13.2732 33.9236L10.7507 34.0414C8.05351 34.1675 5.83236 31.9463 5.95841 29.2491L6.0763 26.7266C6.10526 26.107 5.85661 25.5067 5.398 25.0891L3.53095 23.3887C1.5346 21.5706 1.53461 18.4295 3.53095 16.6114L5.398 14.911C5.85661 14.4934 6.10526 13.8931 6.0763 13.2735L5.95841 10.751C5.83236 8.05375 8.05352 5.83261 10.7507 5.95866L13.2732 6.07655C13.8929 6.10551 14.4931 5.85686 14.9108 5.39825L16.6111 3.53119ZM21.5401 5.21451C20.7137 4.30708 19.2859 4.30708 18.4595 5.21451L16.7592 7.08156C15.8403 8.0905 14.5197 8.63753 13.1565 8.57382L10.634 8.45593C9.40801 8.39864 8.39839 9.40825 8.45569 10.6343L8.57358 13.1568C8.63729 14.5199 8.09026 15.8406 7.08132 16.7594L5.21426 18.4597C4.30683 19.2861 4.30683 20.714 5.21426 21.5404L7.08132 23.2407C8.09026 24.1595 8.63729 25.4802 8.57358 26.8433L8.45569 29.3658C8.39839 30.5919 9.40801 31.6015 10.634 31.5442L13.1565 31.4263C14.5197 31.3626 15.8403 31.9096 16.7592 32.9185L18.4595 34.7856C19.2859 35.693 20.7137 35.693 21.5401 34.7856L23.2404 32.9185C24.1593 31.9096 25.4799 31.3626 26.8431 31.4263L29.3656 31.5442C30.5916 31.6015 31.6012 30.5919 31.5439 29.3658L31.426 26.8433C31.3623 25.4802 31.9094 24.1595 32.9183 23.2407L34.7853 21.5404C35.6928 20.714 35.6928 19.2861 34.7853 18.4597L32.9183 16.7594C31.9094 15.8406 31.3623 14.5199 31.426 13.1568L31.5439 10.6343C31.6012 9.40825 30.5916 8.39864 29.3656 8.45593L26.8431 8.57382C25.4799 8.63753 24.1593 8.0905 23.2404 7.08156L21.5401 5.21451Z'
      fill='#089B19'
    />
    <path
      fillRule='evenodd'
      clipRule='evenodd'
      d='M14.1159 19.1162C14.6041 18.628 15.3955 18.628 15.8837 19.1162L18.3331 21.5656L24.1159 15.7828C24.6041 15.2947 25.3955 15.2947 25.8837 15.7828C26.3718 16.271 26.3718 17.0624 25.8837 17.5506L19.217 24.2173C18.7289 24.7054 17.9374 24.7054 17.4493 24.2173L14.1159 20.8839C13.6278 20.3958 13.6278 19.6043 14.1159 19.1162Z'
      fill='#089B19'
    />
  </svg>
);
